import GroupAdminAnalytics from '@/components/pages/analytics/GroupAdminAnalytics';
import StaffMonitor from '@/components/pages/kiosk/monitor/StaffMonitor/StaffMonitor';
import StaffMonitorAccess from '@/components/pages/kiosk/monitor/StaffMonitor/StaffMonitorAccess';
import StaffCostAccess from '@/components/pages/kiosk/schedule/staff/StaffCostAccess';
import { useSubscription } from '@apollo/client';
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider as MUIThemeProvider } from '@material-ui/styles';
import React, { useContext, useState } from 'react';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import { Redirect, Route, Switch } from 'react-router-dom';
import { CenteredSpinner } from '@/se/components/Spinner';
import { tabletThemeLight } from '@/themes/tabletTheme';
import GridFader from '../../core/GridFader';
import Monitor from './monitor/MonitorModern';
import WaitingRoomMonitor from './WaitingRoomMonitor';
import RoomTabletAppFactory from './RoomTabletApp.tsx';
import Schedule from './SchedulePage';
import { screenConfiguration } from '@/graph/screens';
import ConnectionProvider from './ConnectionProvider';
import BlockSchedulePage from '../blockSchedule/BlockSchedulePage';
import LegacyBlockSchedulePage from '../legacyBlockSchedule/LegacyBlockSchedulePage';
import ReviewsPage from './reviews/ReviewsPage';
import ExitPage from './exit/ExitPage';
import { ROOM_TYPES } from '../../entities/room/enums';
import MultipleCalendarPage from '../calendar/MultipleCalendarPage';
import ChartingProfile from './charting/screens/ChartingProfile';
import { SetChartingPinTimeout } from './charting/Charting';
import ChartingPage from './charting/ChartingPage';
import { get } from 'lodash/fp';
import { QuestionnaireType } from '@/types/Questionnaire';
import DarkThemeProviderWrapper from '../../../DarkThemeProviderWrapper';
import WhiteThemeProviderWrapper from '../../../WhiteThemeProviderWrapper';
import ChartingTimeout from './charting/ChartingTimeout';
import OperationRoomTablet from './tablet/OperationRoomTablet';
import Vitals from '../inject/Vitals';
import VitalsSignature from '../inject/VitalsSignature';
import WaitingRoomTablet from './tablet/waitingRoom/WaitingRoomTablet';
import StaffingCostPage from './schedule/staff/StaffingCostPage';
import AnesthesiaTable from '../inject/AnesthesiaTable';
import AnesthesiaVitals from '../inject/AnesthesiaVitals';
import Dictation from './charting/screens/Dictation';
import { Scope, ScopeProvider } from '@/contexts/ScopeContext';
import AnalyticsScreen from '../analytics/AnalyticsScreen';
import { muiThemeLight } from '@/muiTheme';
import { ThemeProvider } from 'styled-components';
import { light as lightTheme } from '../../../theme';
import { ScreenConfigurationProvider } from '@/contexts/ScreenConfigurationContext';
import StaffLogPage from '@/components/pages/staff-log/StaffLogPage';
import { withSession } from '@/state/Session';
import { UnauthorizedScreen } from '@/components/pages/kiosk/UnauthorizedScreen';
import { mapUnauthorizedError } from '@/essentials/mapUnauthorizedError';

export const defaultConfig = {
  showProcedure: true,
  dischargeSound: true,
  orReadySound: true,
  waitingReadySound: true,
  pacuReadySound: true,
  prepReadySound: true,
  readyForSurgeonSound: true,
  readyToPickupSound: true,
  holdProcedureSound: true,
  blockNerveSound: true,
  useCssGridLayout: false,
  showPostOp: true,
  chartingPinTimeout: 60,
  questionnaireSessionTimeout: 30,
  showRideHome: false,
  showCaretakerWr: false,
  showPreOpWr: true,
  showIntakeWr: true,
  helpSound: true,
  procedureEndSound: false,
  closingSound: false,
};

export const HospitalTabletApp = RoomTabletAppFactory();
export const PrepTabletApp = RoomTabletAppFactory(ROOM_TYPES.PRE_OP);
export const PacuTabletApp = RoomTabletAppFactory(ROOM_TYPES.PACU);
export const PostOpTabletApp = RoomTabletAppFactory(ROOM_TYPES.POST_OP);

// Inner component that uses Apollo hooks - must be rendered inside ConnectionProvider
const ScreenContent = ({ match, location, isHuman, inject, path, organizationId, accessToken, onUnauthorized }) => {
  const scope = useContext(Scope);
  const queryParams = queryString.parse(location.search);

  // useSubscription must be called inside Apollo Provider (ConnectionProvider)
  const { data, loading } = useSubscription(screenConfiguration);
  const screenConfig = data?.screenConfiguration;
  const configuration = isHuman ? defaultConfig : screenConfig;

  // Handle screen removal: if subscription returns null (not loading), screen was removed from admin
  if (!isHuman && !loading && screenConfig === null) {
    // Trigger disconnect to redirect to authorization screen
    if (onUnauthorized) {
      onUnauthorized(new Error('Screen configuration removed'));
    }
    return <CenteredSpinner size={'5rem'} />;
  }

  const isConfigurationLoading = !isHuman && (loading || !screenConfig);
  const useLegacyBlockSchedule = scope?.hospital?.modules?.legacyBlockSchedule;

  if (isConfigurationLoading) {
    return <CenteredSpinner size={'5rem'} />;
  }

  return (
    <>
      <Route path={`${match.path}/charting`} component={ChartingTimeout} />
      <ScreenConfigurationProvider value={configuration}>
        <SetChartingPinTimeout value={configuration?.chartingPinTimeout} />

        {inject ? (
          <Switch component={GridFader}>
            <Route
              path={`${match.path}/anesthesia-table/:patientId`}
              render={props => {
                const patientId = parseInt(get('params.patientId')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <AnesthesiaTable hospitalId={organizationId} patientId={patientId} />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/anesthesia-vitals/:patientId`}
              render={props => {
                const patientId = parseInt(get('params.patientId')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <AnesthesiaVitals hospitalId={organizationId} patientId={patientId} />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/vitals/:patientId`}
              render={props => {
                const patientId = parseInt(get('params.patientId')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <Vitals hospitalId={organizationId} patientId={patientId} />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/vitals-signature/:patientId`}
              render={props => {
                const patientId = parseInt(get('params.patientId')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <VitalsSignature hospitalId={organizationId} patientId={patientId} />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
          </Switch>
        ) : (
          <Switch component={GridFader}>
            <Route
              path={`${match.path}/monitor`}
              render={() => {
                const isLightMode = configuration?.lightMode || false;

                if (isLightMode) {
                  return (
                    <ThemeProvider theme={lightTheme}>
                      <MUIThemeProvider theme={muiThemeLight}>
                        <CssBaseline />
                        <Monitor
                          accessToken={accessToken}
                          version={queryParams.version}
                          configuration={configuration}
                          isKiosk={!isHuman}
                        />
                      </MUIThemeProvider>
                    </ThemeProvider>
                  );
                }

                return (
                  <DarkThemeProviderWrapper>
                    <Monitor
                      accessToken={accessToken}
                      version={queryParams.version}
                      configuration={configuration}
                      isKiosk={!isHuman}
                    />
                  </DarkThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/monitor-staff`}
              render={() => (
                <DarkThemeProviderWrapper>
                  <StaffMonitorAccess>
                    <StaffMonitor />
                  </StaffMonitorAccess>
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/group-admin`}
              render={() => (
                <DarkThemeProviderWrapper>
                  <GroupAdminAnalytics />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/waiting-room`}
              render={() => (
                <DarkThemeProviderWrapper>
                  <WaitingRoomMonitor version={queryParams.version} configuration={configuration} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/nursing/prep/:roomId`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <PrepTabletApp {...props} configuration={configuration} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/nursing/pacu/:roomId`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <PacuTabletApp {...props} configuration={configuration} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/nursing/post-op/:roomId`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <PostOpTabletApp {...props} configuration={configuration} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/tablet/:operationRoomId`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <OperationRoomTablet {...props} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/block-schedule`}
              render={props => (
                <DarkThemeProviderWrapper>
                  {useLegacyBlockSchedule ? (
                    <LegacyBlockSchedulePage {...props} organizationId={organizationId} />
                  ) : (
                    <BlockSchedulePage {...props} organizationId={organizationId} />
                  )}
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/calendar`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <MultipleCalendarPage {...props} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/schedule/staffing-cost`}
              render={routeProps => (
                <StaffCostAccess>
                  <StaffingCostPage {...routeProps} />
                </StaffCostAccess>
              )}
            />
            <Route
              path={`${match.path}/schedule`}
              render={() => (
                <DarkThemeProviderWrapper>
                  <Schedule isKiosk={!isHuman} config={screenConfig} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/analytics`}
              render={() => (
                <DarkThemeProviderWrapper>
                  <AnalyticsScreen />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/charting/preop-chart/:id`}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <ChartingProfile
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.PreOpChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting/or-chart/:id`}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <ChartingProfile
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.OrChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting/pacu-chart/:id`}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <ChartingProfile
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.PacuChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting/physician-chart/:id`}
              exact={true}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <ChartingProfile
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.PhysicianChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting/physician-chart/:id/dictation`}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <Dictation
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.PhysicianChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting/anesthesiologist-preop-chart/:id`}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <ChartingProfile
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.AnesthesiologistPreOpChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting/anesthesiologist-or-chart/:id`}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <ChartingProfile
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.AnesthesiologistOrChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting/anesthesiologist-pacu-chart/:id`}
              render={props => {
                const patientId = parseInt(get('params.id')(props.match), 10);

                return (
                  <WhiteThemeProviderWrapper>
                    <ChartingProfile
                      {...props}
                      hospitalId={organizationId}
                      patientId={patientId}
                      questionnaireType={QuestionnaireType.AnesthesiologistPacuChart}
                    />
                  </WhiteThemeProviderWrapper>
                );
              }}
            />
            <Route
              path={`${match.path}/charting`}
              render={props => (
                <WhiteThemeProviderWrapper>
                  <ChartingPage {...props} organizationId={organizationId} />
                </WhiteThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/reviews`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <ReviewsPage {...props} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/exit`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <ExitPage {...props} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/waiting-room-tablet`}
              render={() => (
                <ThemeProvider theme={tabletThemeLight}>
                  <CssBaseline />
                  <WaitingRoomTablet configuration={configuration} />
                </ThemeProvider>
              )}
            />
            <Route
              path={`${match.path}/caretaker-communication`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <HospitalTabletApp {...props} hospitalOverviewMode={true} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/staff-log/:id/:role/:date`}
              render={props => (
                <DarkThemeProviderWrapper>
                  <StaffLogPage {...props} />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route
              path={`${match.path}/staff-monitor-table`}
              render={() => (
                <DarkThemeProviderWrapper>
                  <StaffMonitor tableOnly hideCost disableRowClick />
                </DarkThemeProviderWrapper>
              )}
            />
            <Route render={() => <Redirect to={`${match.url}${path || '/monitor'}`} />} />
          </Switch>
        )}
      </ScreenConfigurationProvider>
    </>
  );
};

const ScreenRouter = withSession(s => ({ logOut: s.destroy }))(({
  match,
  location,
  accessToken,
  organizationId,
  logOut,
  path,
  isHuman,
  inject,
  onUnauthorized: kioskOnUnauthorized,
}) => {
  const [unauthorized, setUnauthorized] = useState(null);

  const handleUnauthorized = error => {
    // For kiosks, trigger disconnect to redirect to auth screen
    // For humans, show unauthorized screen with manual refresh options
    if (!isHuman && kioskOnUnauthorized) {
      kioskOnUnauthorized(error);
    } else {
      setUnauthorized(mapUnauthorizedError(error));
    }
  };

  if (unauthorized) {
    return <UnauthorizedScreen description={unauthorized} logOut={logOut} />;
  }
  return (
    <ConnectionProvider accessToken={accessToken} organizationId={organizationId} onUnauthorized={handleUnauthorized}>
      <ScopeProvider>
        <ScreenContent
          match={match}
          location={location}
          isHuman={isHuman}
          inject={inject}
          path={path}
          organizationId={organizationId}
          accessToken={accessToken}
          onUnauthorized={handleUnauthorized}
        />
      </ScopeProvider>
    </ConnectionProvider>
  );
});

export default withRouter(ScreenRouter);
